import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import { Basket } from '../types/basket.type';
import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { ADMIN_BASKETS_API_PATHS } from '../constants/baskets-api-paths.constants';

export interface GetBasketsRequest {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  filters: {
    name?: string;
    category?: string;
    subCategory?: string;
    isActive?: boolean;
    archived?: boolean;
  };
}

type UseAdminGetBasketsResult = {
  isLoading: boolean;
  isRefetching: boolean;
  totalCount: number;
  baskets: Basket[];
  refetch: () => Promise<void>;
};

const INITIAL_STATE: Omit<UseAdminGetBasketsResult, 'refetch'> = {
  isLoading: true,
  isRefetching: false,
  totalCount: 0,
  baskets: [],
};

export function useAdminGetBaskets({
  page,
  limit,
  sortBy,
  sortDirection,
  filters,
}: GetBasketsRequest): UseAdminGetBasketsResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState(INITIAL_STATE);

  const serializedFilters = JSON.stringify(filters);

  const refetch = useCallback(async (): Promise<void> => {
    setResult((previous) => (previous.isLoading ? previous : { ...previous, isRefetching: true }));

    try {
      const response = await HttpClient.get<{ totalCount: number; baskets: Basket[] }>(
        ADMIN_BASKETS_API_PATHS.GET_BASKETS,
        { params: { page, limit, sortBy, sortDirection, filters: serializedFilters } }
      );

      setResult({
        isLoading: false,
        isRefetching: false,
        totalCount: response.totalCount,
        baskets: response.baskets,
      });
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(error, 'Une erreur est survenue lors de la récupération des paniers'),
        { variant: 'error' }
      );
      setResult({ isLoading: false, isRefetching: false, totalCount: 0, baskets: [] });
    }
  }, [page, limit, sortBy, sortDirection, serializedFilters, enqueueSnackbar]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...result, refetch };
}
